/**
 * mensajesPaciente (portal) — espejo de `functions/src/utils/mensajesPaciente.ts`.
 *
 * Define los mensajes de rechazo que ve el PACIENTE y sus valores por defecto,
 * más metadatos para renderizar el editor en el portal (etiqueta, ayuda,
 * variables permitidas). Mantener sincronizado con el backend.
 */

export type MensajeKey =
  | 'capacidadReintento'
  | 'capacidadManana'
  | 'sinMedicos'
  | 'antesDeApertura'
  | 'salaCerrada'
  | 'bufferCierre';

/** Longitud máxima aceptada por plantilla (coincide con el backend). */
export const MENSAJE_MAX_LEN = 400;

export interface MensajeMeta {
  key: MensajeKey;
  /** Etiqueta corta para el editor. */
  label: string;
  /** Cuándo se muestra este mensaje al paciente. */
  cuando: string;
  /** Texto por defecto (idéntico al backend). */
  default: string;
  /** Variables que el sistema reemplaza en tiempo real. */
  variables: Array<{ token: string; desc: string }>;
}

export const MENSAJES: MensajeMeta[] = [
  {
    key: 'capacidadReintento',
    label: 'Cola llena — reintentar hoy',
    cuando: 'La cola está llena pero el día todavía alcanza para atender más pacientes.',
    default:
      'Hay alta demanda en este momento. Intente de nuevo en aproximadamente {tiempo}; los cupos se van liberando a medida que se atiende a los pacientes.',
    variables: [{ token: '{tiempo}', desc: 'tiempo estimado (p. ej. «10 minutos» o «1 hora(s)»)' }],
  },
  {
    key: 'capacidadManana',
    label: 'Cola llena — intentar mañana',
    cuando: 'La cola está llena y lo que queda del día ya no alcanza para vaciarla.',
    default:
      'Por hoy ya no hay campos disponibles. La atención se reanuda mañana; intente nuevamente entonces.',
    variables: [],
  },
  {
    key: 'sinMedicos',
    label: 'Sin médicos disponibles',
    cuando: 'No hay médicos en turno en la sala en este momento.',
    default:
      'En este momento no hay médicos disponibles en esta sala. Intente de nuevo en unos minutos.',
    variables: [],
  },
  {
    key: 'antesDeApertura',
    label: 'Antes de abrir',
    cuando: 'El paciente intenta entrar antes de que abra el próximo bloque de horario.',
    default: 'La sala abre a las {hora}. Vuelva a intentarlo más tarde.',
    variables: [{ token: '{hora}', desc: 'hora de apertura (p. ej. «14:00»)' }],
  },
  {
    key: 'salaCerrada',
    label: 'Sala cerrada por hoy',
    cuando: 'El horario del día ya terminó.',
    default:
      'La sala ya cerró por hoy. La atención se reanuda mañana; intente nuevamente entonces.',
    variables: [],
  },
  {
    key: 'bufferCierre',
    label: 'Por cerrar (buffer)',
    cuando: 'Se dejan de aceptar pacientes en los últimos minutos antes del cierre (buffer).',
    default:
      'La sala está por cerrar y ya no acepta nuevos pacientes por hoy (últimos {minutos} minutos). La atención se reanuda mañana; intente nuevamente entonces.',
    variables: [{ token: '{minutos}', desc: 'minutos del buffer de cierre' }],
  },
];

/** Mapa key → default (conveniencia). */
export const MENSAJES_DEFAULT: Record<MensajeKey, string> = MENSAJES.reduce(
  (acc, m) => ((acc[m.key] = m.default), acc),
  {} as Record<MensajeKey, string>,
);
